import { clamp, hash01, lerp, mixRgb, smoothstep, valueNoise } from '@/core/rng';
import { CHUNK_SIZE, type ChunkData, type Instance } from './chunk';
import type { OverlaySnapshot } from '@/sim/persistence/snapshot';

/**
 * The scar overlay: a sparse diff on top of the pure baseline world.
 *
 * The baseline is a function of position and seed and is never touched.
 * Disturbances (burns, and later anything else) live here as a small set of
 * lattice cells, each with a severity and a birth time. A scar decays on the
 * world clock and vanishes; because chunks rebuild from the pure baseline on
 * invalidation, the land regreens by simply forgetting.
 *
 * Cells are axis-aligned on the SCAR_CELL grid, so each cell lies inside
 * exactly one chunk — and border vertices shared by neighbouring chunks
 * sample the same cell, so chunk edges never disagree about a burn.
 */

export const SCAR_CELL = 32;          // metres, the disturbance lattice
export const REGEN = 40 * 86400;      // full regreen in ~40 world-days

export interface ScarInfo { sev: number; age01: number }

interface ScarCell { gx: number; gz: number; sev: number; at: number }

const CELLS_PER_CHUNK = CHUNK_SIZE / SCAR_CELL;
const cellChunk = (gx: number, gz: number) =>
  `${Math.floor(gx / CELLS_PER_CHUNK)},${Math.floor(gz / CELLS_PER_CHUNK)}`;

/**
 * Every chunk a cell can *appear* in, which since `burnAt` is no longer the
 * cell's own square is not the same as the chunk it *lives* in.
 *
 * `burnAt` interpolates against the neighbouring cell centres, so a scar in a
 * cell on a chunk border tints and thins up to one cell into the chunk next
 * door. If invalidation still named only the owning chunk, that neighbour
 * would keep its stale mesh until something unrelated rebuilt it — a burn
 * clipped dead straight at a seam, and *which* seam would depend on the order
 * the player happened to fly. Naming the 3×3 cell neighbourhood's chunks costs
 * at most four keys per scar and closes it.
 *
 * (`world/wear.ts` has the same latent hole for the same reason and has never
 * been bitten, because a camp's pressure cells are interior by construction.
 * A fire's are not: it spreads until it runs out of fuel, seams included.)
 */
function addReach(into: Set<string>, gx: number, gz: number): void {
  for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
    into.add(cellChunk(gx + dx, gz + dz));
  }
}

export class Overlay {
  private readonly cells = new Map<string, ScarCell>();
  private version_ = 0;

  addScar(wx: number, wz: number, sev: number, at: number): void {
    const gx = Math.floor(wx / SCAR_CELL), gz = Math.floor(wz / SCAR_CELL);
    const key = `${gx},${gz}`;
    const prev = this.cells.get(key);
    // a reburn restarts the clock and keeps the worst severity
    const s = clamp(prev ? Math.max(prev.sev, sev) : sev, 0, 1);
    this.cells.set(key, { gx, gz, sev: s, at });
    this.version_++;
  }

  /** The scar under a world position, or null once healed. */
  scarAt(wx: number, wz: number, now: number): ScarInfo | null {
    const c = this.cells.get(`${Math.floor(wx / SCAR_CELL)},${Math.floor(wz / SCAR_CELL)}`);
    if (!c) return null;
    const age01 = (now - c.at) / REGEN;
    if (age01 >= 1) return null;
    return { sev: c.sev, age01: clamp(age01, 0, 1) };
  }

  /** Remove fully-healed scars; return chunk keys ("cx,cz") they occupied. */
  healedSweep(now: number): string[] {
    const dirty = new Set<string>();
    for (const [key, c] of this.cells) {
      if (now - c.at >= REGEN) {
        this.cells.delete(key);
        addReach(dirty, c.gx, c.gz);
      }
    }
    if (dirty.size > 0) this.version_++;
    return [...dirty];
  }

  /** All chunk keys currently carrying scars. */
  scarChunks(): string[] {
    const keys = new Set<string>();
    for (const c of this.cells.values()) addReach(keys, c.gx, c.gz);
    return [...keys];
  }

  /**
   * The four cells whose centres bracket a point, for `burnAt`. Kept on the
   * class because `cells` is private, and deliberately NOT part of `scarAt`'s
   * contract — see `burnAt`.
   */
  sevAt(gx: number, gz: number, now: number): { sev: number; age01: number } | null {
    const c = this.cells.get(`${gx},${gz}`);
    if (!c) return null;
    const age01 = (now - c.at) / REGEN;
    if (age01 >= 1) return null;
    return { sev: c.sev, age01: clamp(age01, 0, 1) };
  }

  get size(): number { return this.cells.size; }

  /** Cache tag, not history: bumps whenever the scar set changes. */
  get version(): number { return this.version_; }

  /** Every live scar, copied out in insertion order (spec §49). */
  toSnapshot(): OverlaySnapshot {
    return { cells: [...this.cells.values()].map((c) => ({ ...c })) };
  }

  /** Rebuild an overlay from a snapshot. Shares nothing with the source. */
  static fromSnapshot(snap: OverlaySnapshot): Overlay {
    const overlay = new Overlay();
    for (const c of snap.cells) overlay.cells.set(`${c.gx},${c.gz}`, { ...c });
    return overlay;
  }
}

const CHAR = [26 / 255, 22 / 255, 20 / 255] as const;   // fresh burn
const ASH = [96 / 255, 90 / 255, 84 / 255] as const;    // the grey that follows

/**
 * How far past a cell's own edge the burn is allowed to wander, and how hard.
 * `EDGE_SCALE` is metres per noise period: it must be well under SCAR_CELL or
 * the swing is just a second, larger lattice. 11 m is `wear.ts`'s number and
 * for the same reason — it is the scale at which a real fire edge wanders
 * around a wet hollow or a bare patch.
 */
const EDGE_SCALE = 11, EDGE_SWING = 0.38;
const S_EDGE = 0x5b13, S_BURN = 0x77a1;

/**
 * The band over which the understory goes from standing to gone. The cull was
 * a step at 0.55 — every plant under 2.5 m inside the cell dead, every plant a
 * metre outside untouched — which is the single line of code that drew the
 * square the player actually sees. Widening it to a band and dithering across
 * that band with the plant's own position turns the same information into a
 * ragged edge: full kill in the heart of the burn, thinning survivors toward
 * the rim, nothing beyond it.
 */
const BURN_LO = 0.30, BURN_HI = 0.78;

/**
 * WHAT THE EYE SEES OF A BURN — a soft, irregular reading of the same scars
 * `scarAt` reports crisply.
 *
 * The simulation may be gridded; the player's experience must not be. Those
 * are two different questions and they need two different functions, which is
 * why this exists beside `scarAt` rather than replacing it:
 *
 *  · **`scarAt` is the world's own answer** and it stays exactly as it was —
 *    one cell, one severity, a hard edge. `sim/social/habitat.ts:94-95` scores
 *    ground with it (so settlement and migration outcomes hang off it),
 *    `sim/fire.ts:125` reads it to decide what will still burn, and
 *    `test/burn.ts` uses it as an independent second witness against the fire
 *    episode's own account. Smoothing it moves all three. Measured: doing so
 *    fails `test/burn.ts` outright — "the Overlay independently holds 16 scars
 *    for an episode claiming 7" — because bilinear widens the non-null
 *    footprint into a bleed ring the scan then counts.
 *  · **`burnAt` is what it looks like**, and nothing but the chunk transform
 *    and the horizon mesh may call it.
 *
 * Two treatments, both pure functions of world position — no camera, no clock,
 * no visit order — so a burn has the same outline at every LOD, on every
 * machine, and in every rebuild:
 *
 *  1. **Bilinear against the four bracketing cell CENTRES**, which turns the
 *     lattice of plateaux into a continuous field. This is `wear.ts:113-126`'s
 *     `useAt` verbatim in shape; a camp's outline was fixed this way already.
 *  2. **A value-noise swing finer than the lattice**, which turns the smooth
 *     result's contours ragged. Without it a burn is merely a rounded square —
 *     still legibly a cell, just with softer corners.
 *
 * Age is the one field that must NOT be interpolated the same way. A missing
 * neighbour is not "a scar of age zero", it is *no scar*, and averaging
 * against it reads as a burn that is already ashing: `scarTint` mixes CHAR
 * toward the pale ASH on `age01`, so an interpolated 0.25 in fresh ground made
 * a burn render LIGHTER than the land around it (`test/farfield.ts`, screen
 * luminance 0.140 → 0.205, the assertion inverted). So age is averaged over
 * only the cells that actually carry a scar, with the weights renormalised.
 */
export function burnAt(
  overlay: Overlay, wx: number, wz: number, now: number,
): ScarInfo | null {
  const fx = wx / SCAR_CELL - 0.5, fz = wz / SCAR_CELL - 0.5;
  const i = Math.floor(fx), j = Math.floor(fz);
  const u = fx - i, v = fz - j;
  const w = [(1 - u) * (1 - v), u * (1 - v), (1 - u) * v, u * v];
  const at = [
    overlay.sevAt(i, j, now), overlay.sevAt(i + 1, j, now),
    overlay.sevAt(i, j + 1, now), overlay.sevAt(i + 1, j + 1, now),
  ];

  let sev = 0, ageNum = 0, ageDen = 0;
  for (let k = 0; k < 4; k++) {
    const c = at[k];
    if (!c) continue;
    sev += w[k] * c.sev;
    ageNum += w[k] * c.age01;
    ageDen += w[k];
  }
  if (sev <= 0 || ageDen <= 0) return null;

  sev *= lerp(1 - EDGE_SWING, 1 + EDGE_SWING,
              valueNoise(wx / EDGE_SCALE, wz / EDGE_SCALE, S_EDGE));
  return { sev: clamp(sev, 0, 1), age01: ageNum / ageDen };
}

/**
 * Stamp the overlay onto freshly built chunk data, on the main thread.
 * Mutates in place and returns the same object. The baseline underneath is
 * untouched — rebuild the chunk without the overlay and the land is whole.
 */
/**
 * What a burn does to the colour of the ground: char first, ash next, then the
 * base colour reclaims the cell as the scar ages out.
 *
 * Exported for the same reason as `envTint` — the horizon mesh has to be able
 * to draw a burn scar, and one copy of this arithmetic is better than two.
 */
export function scarTint(
  base: readonly [number, number, number],
  scar: { sev: number; age01: number },
): [number, number, number] {
  const target = mixRgb(CHAR, ASH, clamp(scar.age01 / 0.45, 0, 1));
  const w = scar.sev * (1 - smoothstep(0.45, 1, scar.age01));
  return mixRgb([base[0], base[1], base[2]], target, w);
}

export function applyOverlay(
  data: ChunkData,
  overlay: Overlay,
  now: number,
  /** Places a fire may not take — see `world/things.ts`. */
  spare?: (x: number, z: number) => boolean,
): ChunkData {
  if (overlay.size === 0) return data;
  const step = CHUNK_SIZE / (data.res - 1);
  const ox = data.cx * CHUNK_SIZE, oz = data.cz * CHUNK_SIZE;

  // ground: char first, ash next, then the base colour reclaims the ground
  for (let j = 0; j < data.res; j++) for (let i = 0; i < data.res; i++) {
    const scar = burnAt(overlay, ox + i * step, oz + j * step, now);
    if (!scar) continue;
    const k = (j * data.res + i) * 3;
    const c = scarTint([data.colour[k], data.colour[k + 1], data.colour[k + 2]], scar);
    data.colour[k] = c[0]; data.colour[k + 1] = c[1]; data.colour[k + 2] = c[2];
  }

  /*
   * Vegetation: the understory burns away, the canopy stands as charred snags.
   *
   * **Its ENVELOPE never shrinks, but its foliage now goes** (2026-08-20).
   * Until this pass the line above read "structure never shrinks — snags keep
   * their size, only their colour dies", and it was doing real damage: a burnt
   * tree was a green tree painted black, crown intact, silhouette unchanged,
   * casting a full canopy shadow across a scar. What burns off a tree is its
   * leaves. `char` and `charAge` are handed to the renderer, which eats the
   * foliage where it stood (`render/vegetation.ts`, the burn morph) and leaves
   * the skeleton — so the plant's `scaleY` and `scaleXZ` are untouched and
   * everything that reads them still reads the tree that grew there, which is
   * what "structure never shrinks" was protecting and still holds.
   */
  const kept: Instance[] = [];
  for (const inst of data.vegetation) {
    const scar = burnAt(overlay, inst.x, inst.z, now);
    if (!scar) { kept.push(inst); continue; }
    const burn = scar.sev * (1 - scar.age01);
    if (burn <= BURN_LO) { kept.push(inst); continue; }
    // small plants burned away — but a thing history has made important is
    // the one the fire went round, which is exactly the kind of thing a
    // mortal who sees it will need to explain to themselves
    //
    // The take is a DITHER across a band, not a threshold: `hash01` of the
    // plant's own position against how hard this ground burned. In the heart
    // of a burn the odds are 1 and everything small goes; toward the rim
    // survivors thin out instead of ending on a line. Keying the hash on the
    // plant rather than on a counter is what `wear.ts:210-217` does and for
    // the same reason — the same plant is taken or spared at every LOD and in
    // every rebuild, so a fire's edge does not shimmer when you fly over it.
    if (inst.scaleY < 2.5 && !spare?.(inst.x, inst.z) &&
        hash01(Math.round(inst.x * 8), Math.round(inst.z * 8), S_BURN)
          < smoothstep(BURN_LO, BURN_HI, burn)) continue;
    // What survives is charred *by how hard its own ground burned*, so the
    // canopy darkens through the rim instead of every snag inside the burn
    // being the same near-black and every tree outside it untouched. The old
    // constants are what `char` reaches at 1, so the heart of a burn looks
    // exactly as it did.
    const char = smoothstep(BURN_LO, BURN_HI, burn);
    // …and the renderer is told, so the crown can actually go
    inst.char = char;
    inst.charAge = scar.age01;
    const c = inst.colour;
    const grey = (c[0] + c[1] + c[2]) / 3;     // slight grey shift, then near-black
    inst.colour = [
      lerp(c[0], lerp(c[0], grey, 0.6) * 0.18, char),
      lerp(c[1], lerp(c[1], grey, 0.6) * 0.18, char),
      lerp(c[2], lerp(c[2], grey, 0.6) * 0.18, char),
    ];
    kept.push(inst);
  }
  data.vegetation = kept;
  return data;
}
